// General
import { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
// Static Data
import routeConst from "./const/routeConst";
import verificationConst from "./const/verificationConst";
import utilityConst from "./const/utilityConst";
// Components
import App from "./App";

const RouteLoader = () => <div className="route-loader"></div>;

const lazyPage = (importer) => {
  const LazyComponent = lazy(importer);

  return function LazyPage(props) {
    return (
      <Suspense fallback={<RouteLoader />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
};

const Terms = lazyPage(() => import("./components/terms/terms"));
const PrivacyPolicy = lazyPage(
  () => import("./components/privacy-policy/privacy-policy")
);
const Login = lazyPage(() => import("./components/login/login"));
const ForgotPassword = lazyPage(
  () => import("./components/forgot-password/forgot-password")
);
const Join = lazyPage(() => import("./components/join/join"));
const IntroductionSignup = lazyPage(
  () => import("./components/signup/introduction-signup/introduction-signup")
);
const PhoneAuth = lazyPage(
  () => import("./components/signup/phone-auth/phone-auth")
);
const PhoneAuthentication = lazyPage(
  () => import("./components/signup/phone-authentication/phone-authentication")
);
const VerifyPhone = lazyPage(
  () => import("./components/signup/verify-phone/verify-phone")
);
const AccountSignup = lazyPage(
  () => import("./components/signup/account-signup/account-signup")
);
const UsernameSignup = lazyPage(
  () => import("./components/signup/username-signup/username-signup")
);
const UsernameSignupComplete = lazyPage(
  () =>
    import("./components/signup/username-signup-complete/username-signup-complete")
);
const InfoSignup = lazyPage(
  () => import("./components/signup/info-signup/info-signup")
);
const TorSignup = lazyPage(
  () => import("./components/signup/tor-signup/tor-signup")
);
const DescriptionSignup = lazyPage(
  () => import("./components/signup/description-signup/description-signup")
);
const LifestyleSignup = lazyPage(
  () => import("./components/signup/lifestyle-signup/lifestyle-signup")
);
const DetailSignup = lazyPage(
  () => import("./components/signup/detail-signup/detail-signup")
);
const DetailsSignup = lazyPage(
  () => import("./components/signup/details-signup/details-signup")
);
const PremiumBenefits = lazyPage(
  () =>
    import("./components/signup/details-signup/premium-benefits/premium-benefits")
);
const SignupSuccess = lazyPage(
  () => import("./components/signup/signup-success/signup-success")
);
const EmailVerification = lazyPage(
  () =>
    import("./components/verification/email-verification/email-verification")
);
const MobileVerification = lazyPage(
  () =>
    import("./components/verification/mobile-verification/mobile-verification")
);
const ProfileVerification = lazyPage(
  () =>
    import("./components/verification/profile-verification/profile-verification")
);
const SugarbookVerifiedOnboarding = lazyPage(
  () =>
    import("./components/verification/sugarbook-verified/sugarbook-verified-onboarding/sugarbook-verified-onboarding")
);
const SugarbookVerifiedStatus = lazyPage(
  () =>
    import("./components/verification/sugarbook-verified/sugarbook-verified-status/sugarbook-verified-status")
);
const PhotoMatching = lazyPage(
  () => import("./components/verification/photo-matching/photo-matching")
);
const PhotoMatchingRejected = lazyPage(
  () =>
    import("./components/verification/photo-matching/photo-matching-rejected/photo-matching-rejected")
);
const PhotoMatchingPending = lazyPage(
  () =>
    import("./components/verification/photo-matching/photo-matching-pending/photo-matching-pending")
);
const CustomerSupportManualVerificationInfo = lazyPage(
  () =>
    import("./components/verification/customer-support-manual-verification-info/customer-support-manual-verification-info")
);
const Upgrade = lazyPage(() => import("./components/upgrade/upgrade/upgrade"));
const UpgradeCheckout = lazyPage(
  () => import("./components/upgrade/upgrade-checkout/upgrade-checkout")
);
const UpgradePayment = lazyPage(
  () => import("./components/upgrade/upgrade-payment/upgrade-payment")
);
const UpgradeStatus = lazyPage(
  () => import("./components/upgrade/upgrade-status/upgrade-status")
);
const AddPaymentMethod = lazyPage(
  () => import("./components/payment/add-payment-method/add-payment-method")
);
const PaymentRedirect = lazyPage(
  () => import("./components/payment/payment-redirect/payment-redirect")
);
const AlphapoPaymentRedirect = lazyPage(
  () =>
    import("./components/payment/alphapo-payment-redirect/alphapo-payment-redirect")
);
const AlphapoPremiumRedirect = lazyPage(
  () =>
    import("./components/payment/alphapo-premium-redirect/alphapo-premium-redirect")
);
const XenopayPremiumRedirect = lazyPage(
  () =>
    import("./components/payment/xenopay-premium-redirect/xenopay-premium-redirect")
);
const GenesisRedirect = lazyPage(
  () => import("./components/payment/genesis-redirect/genesis-redirect")
);
const GenesisPremiumRedirect = lazyPage(
  () =>
    import("./components/payment/genesis-premium-redirect/genesis-premium-redirect")
);
const CardpayRedirect = lazyPage(
  () => import("./components/payment/cardpay-redirect/cardpay-redirect")
);
const CardpayPremiumRedirect = lazyPage(
  () =>
    import("./components/payment/cardpay-premium-redirect/cardpay-premium-redirect")
);
const CentrobillRedirect = lazyPage(
  () => import("./components/payment/centrobill-redirect/centrobill-redirect")
);
const CentrobillPremiumRedirect = lazyPage(
  () =>
    import("./components/payment/centrobill-premium-redirect/centrobill-premium-redirect")
);
const DirectBankTransfer = lazyPage(
  () =>
    import("./components/payment/direct-bank-transfer/direct-bank-transfer")
);
const Search = lazyPage(() => import("./components/search/search/search"));
const Inbox = lazyPage(() => import("./components/inbox/inbox/inbox"));
const ChatConversation = lazyPage(
  () => import("./components/inbox/chat-conversation/chat-conversation")
);
const Live = lazyPage(() => import("./components/live/live/live"));
const Livestream = lazyPage(
  () => import("./components/live/livestream/livestream")
);
const LivestreamEnded = lazyPage(
  () => import("./components/live/livestream-ended/livestream-ended")
);
const Livestreamer = lazyPage(
  () => import("./components/live/livestreamer/livestreamer")
);
const Interests = lazyPage(() => import("./components/interests/interests"));
const ProfileOverview = lazyPage(
  () => import("./components/profile/overview/overview")
);
const UserProfile = lazyPage(
  () => import("./components/profile/view/user-profile")
);
const StreamerCenter = lazyPage(
  () =>
    import("./components/profile/streamer-center/streamer-center/streamer-center")
);
const LastStreamSummary = lazyPage(
  () =>
    import("./components/profile/streamer-center/last-stream-summary/last-stream-summary")
);
const Follows = lazyPage(
  () => import("./components/profile/streamer-center/follows/follows")
);
const ProfileEdit = lazyPage(() => import("./components/profile/edit/edit"));
const ProfileWallet = lazyPage(
  () => import("./components/profile/wallet/wallet")
);
const LevelingCenter = lazyPage(
  () => import("./components/profile/leveling/leveling")
);
const ProfileCompletion = lazyPage(
  () => import("./components/profile/profile-completion/profile-completion")
);
const Settings = lazyPage(
  () => import("./components/settings/settings/settings")
);
const UpdatePassword = lazyPage(
  () => import("./components/settings/update-password/update-password")
);
const UpdateEmail = lazyPage(
  () => import("./components/settings/update-email/update-email/update-email")
);
const UpdateEmailVerification = lazyPage(
  () =>
    import("./components/settings/update-email/update-email-verification/update-email-verification")
);
const BlockedMembers = lazyPage(
  () => import("./components/settings/blocked-members/blocked-members")
);
const PhotoPermissions = lazyPage(
  () => import("./components/settings/photo-permissions/photo-permissions")
);
const VideoCallSettings = lazyPage(
  () =>
    import("./components/settings/video-call-settings/video-call-settings/video-call-settings")
);
const VideoCallSettingsCustomize = lazyPage(
  () => import("./components/settings/video-call-settings/customize/customize")
);
const DeactivateAccount = lazyPage(
  () => import("./components/settings/deactivate-account/deactivate-account")
);
const VideoCall = lazyPage(
  () => import("./components/video-call/video-call/video-call")
);
const PrivateStandby = lazyPage(
  () => import("./components/video-call/private-standby/private-standby")
);
const VideoCallRoom = lazyPage(
  () => import("./components/video-call/video-call-room/video-call-room")
);
const VideoCallSummary = lazyPage(
  () => import("./components/video-call/video-call-summary/video-call-summary")
);
const VideoCallRate = lazyPage(
  () => import("./components/video-call/video-call-rate/video-call-rate")
);
const VideoCallRecentTab = lazyPage(
  () =>
    import("./components/video-call/video-call/video-call-recent-tab/video-call-recent-tab")
);
const Feed = lazyPage(() => import("./components/feed/feed"));
const Leaderboard = lazyPage(
  () => import("./components/leaderboard/leaderboard/leaderboard")
);
const TopLeaderboardList = lazyPage(
  () =>
    import("./components/leaderboard/top-leaderboard-list/top-leaderboard-list")
);
const Support = lazyPage(() => import("./components/support/support"));
const VerifiedProfileEndScreen = lazyPage(
  () =>
    import("./components/support/verified-profile-end-screen/verified-profile-end-screen")
);
const SafetyTips = lazyPage(
  () => import("./components/safety-tips/safety-tips")
);
const VideoCallTutorial = lazyPage(
  () => import("./components/tutorials/video-call-tutorial/video-call-tutorial")
);
const CoinShop = lazyPage(() => import("./components/coin-shop/coin-shop"));
const CasinoGames = lazyPage(
  () => import("./components/games/casino-games/casino-games")
);
const Logout = lazyPage(() => import("./components/logout/logout"));
const RedirectHandler = lazyPage(
  () => import("./components/redirect-handler/redirect-handler")
);
const Error404Page = lazyPage(() => import("./components/404/404"));
const LivenessVerification = lazyPage(
  () =>
    import("./components/verification/liveness-verification/liveness-verification")
);
const EkycVerificationOnboarding = lazyPage(
  () =>
    import("./components/verification/ekyc-verification/onboarding/ekyc-verification-onboarding")
);
const EkycVerificationOnboardingFrontId = lazyPage(
  () =>
    import("./components/verification/ekyc-verification/front-id/ekyc-front-id")
);
const EkycVerificationBackId = lazyPage(
  () =>
    import("./components/verification/ekyc-verification/back-id/ekyc-back-id")
);
const EkycVerificationIdOverview = lazyPage(
  () =>
    import("./components/verification/ekyc-verification/id-overview/ekyc-id-overview")
);
const EkycDetailsVerification = lazyPage(
  () =>
    import("./components/verification/ekyc-verification/details-verification/details-verification")
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      // General
      { path: routeConst.landing.path, element: <Login /> },
      { path: routeConst.terms.path, element: <Terms /> },
      { path: routeConst.privacy.path, element: <PrivacyPolicy /> },

      // Login
      { path: routeConst.login.path, element: <Login /> },

      // Phone Authentication
      {
        path: routeConst.phoneAuthentication.path,
        element: (
          <PhoneAuthentication type={utilityConst.fillingDetails.signup.type} />
        ),
      },
      // Missing Phone Authentication
      {
        path: routeConst.phoneAuthentication.altPath,
        element: (
          <PhoneAuthentication
            type={utilityConst.fillingDetails.missingDetails.type}
          />
        ),
      },

      // Forgot Password
      { path: routeConst.forgotPassword.path, element: <ForgotPassword /> },

      // Sign Up
      { path: routeConst.join.path, element: <Join /> },
      {
        path: routeConst.introductionSignup.path,
        element: <IntroductionSignup />,
      },
      { path: routeConst.phoneAuth.path, element: <PhoneAuth /> },
      { path: routeConst.verifyPhone.path, element: <VerifyPhone /> },
      { path: routeConst.accountSignup.path, element: <AccountSignup /> },
      {
        path: routeConst.infoSignup.path,
        element: <InfoSignup type={utilityConst.fillingDetails.signup.type} />,
      },
      {
        path: routeConst.usernameSignup.path,
        element: <UsernameSignup />,
      },
      {
        path: routeConst.usernameSignupComplete.path,
        element: <UsernameSignupComplete />,
      },
      {
        path: routeConst.torSignup.path,
        element: <TorSignup />,
      },
      {
        path: routeConst.descriptionSignup.path,
        element: <DescriptionSignup />,
      },
      { path: routeConst.lifestyleSignup.path, element: <LifestyleSignup /> },
      { path: routeConst.detailSignup.path, element: <DetailSignup /> },
      { path: routeConst.detailsSignup.path, element: <DetailsSignup /> },
      { path: routeConst.premiumBenefits.path, element: <PremiumBenefits /> },
      {
        path: routeConst.premiumBenefits.upgrade.path,
        element: <Upgrade showUtilityNavbar={true} />,
      },
      {
        path: routeConst.signupSuccess.path,
        element: <SignupSuccess />,
      },
      {
        path: routeConst.missingDetails.path,
        element: (
          <InfoSignup type={utilityConst.fillingDetails.missingDetails.type} />
        ),
      },

      // Email Verification
      {
        path: routeConst.verify.emailVerification.path,
        element: (
          <EmailVerification type={utilityConst.fillingDetails.signup.type} />
        ),
      },
      // Missing Email Verification
      {
        path: routeConst.verify.emailVerification.altPath,
        element: (
          <EmailVerification
            type={utilityConst.fillingDetails.missingDetails.type}
          />
        ),
      },
      // Mobile Verification
      {
        path: routeConst.verify.mobileVerification.path,
        element: (
          <MobileVerification type={utilityConst.fillingDetails.signup.type} />
        ),
      },
      // Missing Mobile Verification
      {
        path: routeConst.verify.mobileVerification.altPath,
        element: (
          <MobileVerification
            type={utilityConst.fillingDetails.missingDetails.type}
          />
        ),
      },
      // Profile Verification
      {
        path: routeConst.verify.profileVerification.path,
        element: <ProfileVerification />,
      },
      // Sugarbook Verified
      {
        path: routeConst.verify.sugarbookVerified.path,
        element: <LivenessVerification isFromEkyc={false} />,
      },
      // Sugarbook Verified Onboarding
      {
        path: routeConst.verify.sugarbookVerified.onboarding.path,
        element: <SugarbookVerifiedOnboarding />,
      },
      // Sugarbook Verified Status Success
      {
        path: routeConst.verify.sugarbookVerified.status.approved.path,
        element: (
          <SugarbookVerifiedStatus
            status={utilityConst.sugarbookVerifiedStatus.approved}
          />
        ),
      },
      // Sugarbook Verified Status Rejected
      {
        path: routeConst.verify.sugarbookVerified.status.rejected.path,
        element: (
          <SugarbookVerifiedStatus
            status={utilityConst.sugarbookVerifiedStatus.rejected}
          />
        ),
      },
      // Sugarbook Verified Status Duplicate
      {
        path: routeConst.verify.sugarbookVerified.status.rejected.duplicate,
        element: (
          <SugarbookVerifiedStatus
            status={utilityConst.sugarbookVerifiedStatus.duplicate}
          />
        ),
      },
      // Customer Support Mobile Manual Verification Info
      {
        path: routeConst.verify.customerSupportInfo.mobile.path,
        element: (
          <CustomerSupportManualVerificationInfo
            type={verificationConst.manualVerificationType.mobile}
          />
        ),
      },
      // Customer Support Facial Manual Verification Info
      {
        path: routeConst.verify.customerSupportInfo.facial.path,
        element: (
          <CustomerSupportManualVerificationInfo
            type={verificationConst.manualVerificationType.facial}
          />
        ),
      },
      {
        path: routeConst.verify.sugarbookVerified.photoMatching.path,
        element: <PhotoMatching />,
      },
      {
        path: routeConst.verify.sugarbookVerified.photoMatching.rejected.path,
        element: <PhotoMatchingRejected />,
      },
      {
        path: routeConst.verify.sugarbookVerified.photoMatching.pending.path,
        element: <PhotoMatchingPending />,
      },
      {
        path: routeConst.verify.sugarbookVerified.photoMatching
          .customerSupportInfo.path,
        element: (
          <CustomerSupportManualVerificationInfo
            tyle={verificationConst.manualVerificationType.photoMatching}
          />
        ),
      },

      // Upgrade
      { path: routeConst.upgrade.path, element: <Upgrade /> },
      {
        path: routeConst.upgrade.checkout.path,
        element: <UpgradeCheckout />,
      },
      { path: routeConst.upgrade.payment.path, element: <UpgradePayment /> },
      { path: routeConst.upgrade.status.path, element: <UpgradeStatus /> },

      // Payment
      { path: routeConst.payment.add.path, element: <AddPaymentMethod /> },
      { path: routeConst.payment.redirect.path, element: <PaymentRedirect /> },
      {
        path: routeConst.payment.alphapoRedirect.path,
        element: <AlphapoPaymentRedirect />,
      },
      {
        path: routeConst.payment.alphapoPremiumRedirect.path,
        element: <AlphapoPremiumRedirect />,
      },
      {
        path: routeConst.payment.xenopayPremiumRedirect.path,
        element: <XenopayPremiumRedirect />,
      },
      {
        path: routeConst.payment.genesis.path,
        element: <GenesisRedirect />,
      },
      {
        path: routeConst.payment.genesisPremiumRedirect.path,
        element: <GenesisPremiumRedirect />,
      },
      {
        path: routeConst.payment.cardpay.path,
        element: <CardpayRedirect />,
      },
      {
        path: routeConst.payment.cardpayPremiumRedirect.path,
        element: <CardpayPremiumRedirect />,
      },
      {
        path: routeConst.payment.centrobill.path,
        element: <CentrobillRedirect />,
      },
      {
        path: routeConst.payment.centrobillPremiumRedirect.path,
        element: <CentrobillPremiumRedirect />,
      },
      {
        path: routeConst.payment.directBankTransfer.path,
        element: <DirectBankTransfer />,
      },

      // Search
      { path: routeConst.search.path, element: <Search /> },

      // Messages
      { path: routeConst.inbox.path, element: <Inbox /> },
      {
        path: routeConst.inbox.messaging.chat.chatId.path,
        element: <ChatConversation />,
      },

      // Live
      { path: routeConst.live.path, element: <Live /> },
      { path: routeConst.live.liveId.path, element: <Livestream /> },
      { path: routeConst.live.ended.path, element: <LivestreamEnded /> },
      { path: routeConst.live.livestreamer.path, element: <Livestreamer /> },

      // Interest
      { path: routeConst.interest.path, element: <Interests /> },

      // Profile
      {
        path: routeConst.profile.overview.path,
        element: <ProfileOverview />,
      },
      { path: routeConst.profile.view.path, element: <UserProfile /> },
      {
        path: routeConst.profile.streamerCenter.path,
        element: <StreamerCenter />,
      },
      {
        path: routeConst.profile.streamerCenter.lastStreamSummary.path,
        element: <LastStreamSummary />,
      },
      {
        path: routeConst.profile.streamerCenter.follows.path,
        element: <Follows />,
      },
      { path: routeConst.profile.edit.path, element: <ProfileEdit /> },
      { path: routeConst.profile.wallet.path, element: <ProfileWallet /> },
      { path: routeConst.profile.leveling.path, element: <LevelingCenter /> },
      {
        path: routeConst.profile.completion.path,
        element: <ProfileCompletion />,
      },

      // Settings
      { path: routeConst.settings.path, element: <Settings /> },
      {
        path: routeConst.settings.updatePassword.path,
        element: <UpdatePassword />,
      },
      {
        path: routeConst.settings.updateEmail.path,
        element: <UpdateEmail />,
      },
      {
        path: routeConst.settings.updateEmail.verification.path,
        element: <UpdateEmailVerification />,
      },
      {
        path: routeConst.settings.blockedMembers.path,
        element: <BlockedMembers />,
      },
      {
        path: routeConst.settings.photoPermission.path,
        element: <PhotoPermissions />,
      },
      {
        path: routeConst.settings.videoCall.path,
        element: <VideoCallSettings />,
      },
      {
        path: routeConst.settings.videoCall.customize.path,
        element: <VideoCallSettingsCustomize />,
      },
      {
        path: routeConst.settings.deactivateAccount.path,
        element: <DeactivateAccount isVp3={false} />,
      },
      {
        path: routeConst.settings.deactivateAccount.altPath,
        element: <DeactivateAccount isVp3={true} />,
      },

      // Video Call
      { path: routeConst.videoCall.path, element: <VideoCall /> },
      {
        path: routeConst.videoCall.privateStandby.path,
        element: <PrivateStandby />,
      },
      { path: routeConst.videoCall.room.path, element: <VideoCallRoom /> },
      {
        path: routeConst.videoCall.summary.path,
        element: <VideoCallSummary />,
      },
      { path: routeConst.videoCall.rate.path, element: <VideoCallRate /> },
      {
        path: routeConst.videoCall.callHistory.path,
        element: <VideoCallRecentTab />,
      },

      // Feed
      {
        path: routeConst.feed.path,
        element: <Feed />,
      },

      // Leaderboard
      { path: routeConst.leaderboard.path, element: <Leaderboard /> },
      {
        path: routeConst.leaderboard.list.path,
        element: <TopLeaderboardList />,
      },

      // Support
      { path: routeConst.support.path, element: <Support /> },
      { path: routeConst.vp3Support.path, element: <Support isVp3={true} /> },
      {
        path: routeConst.vp3Support.endScreen.path,
        element: <VerifiedProfileEndScreen />,
      },

      // Safety Tips
      { path: routeConst.safetyTips.path, element: <SafetyTips /> },

      // Tutorial
      {
        path: routeConst.tutorials.videoCall.path,
        element: <VideoCallTutorial />,
      },

      // Coin Shop
      { path: routeConst.coinShop.path, element: <CoinShop /> },

      // Games
      {
        path: routeConst.games.casino.path,
        element: (
          <CasinoGames
            showHeader={false}
            headerType={"desktop"}
            isDialog={false}
            hideGameLabelContainerWhenSelected={true}
          />
        ),
      },

      // EKYC
      {
        path: routeConst.verify.ekyc.onboarding.path,
        element: <EkycVerificationOnboarding />,
      },
      {
        path: routeConst.verify.ekyc.frontId.path,
        element: <EkycVerificationOnboardingFrontId />,
      },
      {
        path: routeConst.verify.ekyc.backId.path,
        element: <EkycVerificationBackId />,
      },
      {
        path: routeConst.verify.ekyc.idOverview.path,
        element: <EkycVerificationIdOverview />,
      },
      {
        path: routeConst.verify.ekyc.selfieVerification.path,
        element: <LivenessVerification isFromEkyc={true} />,
      },
      {
        path: routeConst.verify.ekyc.detailsVerification.path,
        element: <EkycDetailsVerification />,
      },

      // Logout
      { path: routeConst.logout.path, element: <Logout /> },

      // Redirect Handler
      { path: routeConst.redirectHandler.path, element: <RedirectHandler /> },

      // Error Page
      { path: "*", element: <Error404Page /> },

      // Super Secret
      {
        path: routeConst.superSecret.liveness.path,
        element: <LivenessVerification />,
      },
    ],
    errorElement: <Error404Page />,
  },
]);

export default router;
